// Render a manifest to a Blob URL — produces a blob: URL usable as an image
// `src`, unlike the canvas-only `renderManifest`. For consumers (e.g. Next.js Image)
// that prefer URL-based rendering over the canvas direct-paint path.
//
// Browser-only. Uses `OffscreenCanvas` when available (faster, off-main-
// thread-friendly); falls back to a hidden `<canvas>` if not.

import { type RenderManifestOptions, renderManifest } from './render';
import type { PatternManifest } from './types';

export interface RenderManifestToBlobUrlOptions extends RenderManifestOptions {
  /** Output canvas width in pixels. Defaults to 1200. */
  readonly width?: number;
  /** Output canvas height in pixels. Defaults to 675. */
  readonly height?: number;
  /** Output MIME type. Defaults to 'image/png'. */
  readonly mimeType?: 'image/png' | 'image/jpeg' | 'image/webp';
  /** Quality for jpeg / webp. Defaults to 0.92. */
  readonly quality?: number;
}

const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 675;
const DEFAULT_QUALITY = 0.92;

function renderManifestOptions(options: RenderManifestToBlobUrlOptions): RenderManifestOptions {
  return {
    ...(options.onFallback !== undefined ? { onFallback: options.onFallback } : {}),
    ...(options.debugOverride !== undefined ? { debugOverride: options.debugOverride } : {}),
    ...(options.theme !== undefined ? { theme: options.theme } : {}),
  };
}

/**
 * Render a manifest to a `Blob` (without creating a URL). Use this when
 * you want to upload, hash, or otherwise process the bytes directly.
 */
export async function renderManifestToBlob(
  manifest: PatternManifest,
  options: RenderManifestToBlobUrlOptions = {},
): Promise<Blob> {
  const width = options.width ?? DEFAULT_WIDTH;
  const height = options.height ?? DEFAULT_HEIGHT;
  const mimeType = options.mimeType ?? 'image/png';
  const quality = options.quality ?? DEFAULT_QUALITY;

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      throw new Error('pattern-studio: failed to acquire 2D context on OffscreenCanvas');
    }
    await renderManifest(ctx, width, height, manifest, renderManifestOptions(options));
    return canvas.convertToBlob({ type: mimeType, quality });
  }

  // Fallback for browsers without OffscreenCanvas (older Safari).
  if (typeof document === 'undefined') {
    throw new Error('pattern-studio: no rendering context available (SSR)');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('pattern-studio: failed to acquire 2D context on fallback canvas');
  }
  await renderManifest(ctx, width, height, manifest, renderManifestOptions(options));
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) reject(new Error('pattern-studio: canvas.toBlob returned null'));
        else resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}
